import { Card, Tabs } from 'antd';
import { returnOnlyNumbers, translateX } from 'utils/helpers';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  JarvislyFormProvider,
  JarvislyTabProvider,
} from 'components/jarvisly-components/others/FormComponents';
import CompaniesRecordIndex from './Record/RecordIndex';
import JarvislyContactsIndex from 'components/jarvisly-components/forms/JarvislyContactsIndex';
import JarvislyContactsModal from 'components/jarvisly-components/forms/modals/JarvislyContactsModal';
import MultiOperatorsParametersIndex from './Parameters/ParametersIndex';
import appService from 'services/appService';
import JarvislyDocumentStatusModal from 'components/jarvisly-components/forms/modals/JarvislyDocumentStatusModal';
import dayjs from 'dayjs';
import useTabParent from 'components/jarvisly-components/hooks/useTabParentHook';

// GLOBAL VARIABLES ************************************************************
// *****************************************************************************

// COMPONENT *******************************************************************
// *****************************************************************************

const AppmultiOperatorsForm = forwardRef((props, ref) => {
  // props deconstruction ------------------------------------------------------
  const { selectedModule, document, _id, setModuleFocus, mode } = props;

  // local variables I ---------------------------------------------------------
  const initTab = selectedModule.documentState.initTab;
  const availableTabs = [initTab, 'contacts', 'parameters'];

  const contactModalRef = useRef(null);
  const parametersTabRef = useRef(null);
  const documentStatusModalRef = useRef(null);

  // component states ----------------------------------------------------------
  const [tab, setTab] = useTabParent(availableTabs, 'company');
  const [entityType, setEntityType] = useState();

  // local variables II --------------------------------------------------------
  const contactsTabProps = {
    ...props,
    showContactModal: contactModalRef?.current?.showModal,
    setShowContactModal: contactModalRef?.current?.setShowModal,
    selectedRecord: contactModalRef?.current?.selectedRecord,
    setSelectedRecord: contactModalRef?.current?.setSelectedRecord,
    entityType,
    setEntityType,
  };

  const childProps = {
    ...props,
    entityType,
    setEntityType,

    selectedTab: tab,
    initTab,
    tab,
  };

  const tabItems = buildTabItens();

  // hooks ---------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    documentStatusModalRef,
  }));

  // methods -------------------------------------------------------------------
  const onTabClick = value => {
    if (!tabItems.find(t => t.key === value)) value = initTab;
    setTab(value);
  };

  const onSave = async ({ body }) => {
    if (!body) return;

    // decorate entity
    body.dataType = 'company';
    body.dataProfile = 'operator';
    body.uniqueKey = returnOnlyNumbers(body.cnpj);

    // PARAMETERS
    if (body?.parameters?.__exchangeRateSuffix) {
      const parameters = document?.parameters || {};
      const exchanges = parameters?.exchanges || [];
      const exchangeCurrency = body?.parameters?.__exchangeRateSuffix || 'brl';
      const exchangeValue = body?.parameters?.__exchangeRate || 1;
      const idx = exchanges.findIndex(e => e.currency === exchangeCurrency);

      if (idx === -1) {
        exchanges.push({
          currency: exchangeCurrency,
          value: exchangeValue,
          changedAt: dayjs(),
          changedByApi: false,
        });
      } else {
        exchanges[idx].value = exchangeValue;

        if (
          body?.parameters?.__exchangeRate !==
          document?.parameters?.__exchangeRate
        ) {
          exchanges[idx].changedAt = dayjs();
          exchanges[idx].changedByApi = false;
        }
      }

      parameters.exchanges = exchanges.map(e => {
        e.default = e.currency === exchangeCurrency;
        return e;
      });
      body.parameters = parameters;
    }

    // ATTACHMENTS
    if (parametersTabRef?.current) {
      const attachments = parametersTabRef?.current?.attachments || [];
      body.attachmentIds = appService.parseAttachmentsState(attachments);

      // discovery conditionsFileUrl
      let attach = appService.getAttachmentUrlByTag('conditions', attachments);
      if (attach !== false) body.conditionsFileUrl = attach;

      // discovery pictureUrl
      attach = appService.getAttachmentUrlByTag('logo', attachments);
      if (attach !== false) body.pictureUrl = attach;
    }

    body.status = document?.status;

    return body;
  };

  // local variables II --------------------------------------------------------

  // UI COMPONENT --------------------------------------------------------------
  return (
    <>
      {/* DOCUMENT STATUS MODAL WRAPPER */}
      <JarvislyDocumentStatusModal {...props} ref={documentStatusModalRef} />
      {/* DOCUMENT STATUS MODAL WRAPPER */}

      {/* CONTACTS MODAL WRAPPER */}
      <JarvislyContactsModal {...childProps} ref={contactModalRef} />
      {/* CONTACTS MODAL WRAPPER */}

      <JarvislyFormProvider
        name="form-operator-register"
        document={document}
        autoFocus="name"
        requiredFields={['name', 'cnpj', 'fullName', 'phone', 'zip', 'number']}
        showSubmitButton={tab !== 'contacts'}
        setTab={setTab}
        onSave={onSave}
      >
        <Tabs
          type="line"
          tabPosition="top"
          activeKey={tab}
          rootClassName="tab-in-form"
          items={tabItems.filter(x => !x.hide)}
          onTabClick={onTabClick}
        />
      </JarvislyFormProvider>
    </>
  );

  // INTERNAL FUNCTIONS ========================================================
  // ===========================================================================

  function buildTabItens() {
    return [
      {
        label: translateX('record'),
        key: initTab,
        hide: false,
        children: (
          <JarvislyTabProvider tab={initTab}>
            <CompaniesRecordIndex {...props} />
          </JarvislyTabProvider>
        ),
      },

      {
        label: translateX('contacts'),
        key: 'contacts',
        hide: false,
        disabled: mode === 'add',
        children: (
          <JarvislyTabProvider tab="contacts">
            <Card className="cad-with-body-no-padding">
              <JarvislyContactsIndex {...contactsTabProps}  />
            </Card>
          </JarvislyTabProvider>
        ),
      },

      {
        label: translateX('parameters'),
        key: 'parameters',
        hide: false,
        disabled: mode === 'add',
        children: (
          <JarvislyTabProvider tab="parameters">
            <MultiOperatorsParametersIndex
              {...props}
              ref={parametersTabRef}
            />
          </JarvislyTabProvider>
        ),
      },
    ];
  }
});

export default AppmultiOperatorsForm;
